import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { connect } from 'react-redux'
import { initial, range } from 'lodash'
import { Panel } from 'react-bootstrap'
import DateSelect from 'components/date-select'
import { StyledSubHeading } from 'components/headings'
import { Checkbox, Form } from 'components/forms'
import { SaveButton } from 'components/buttons'
import { successMessage } from 'lib/flash-message'

import { today } from 'lib/date'
import { createValidator, required } from 'lib/form-validation'

export const EmailPreferencesForm = ({ submitData, initialValues, user }) => {
  const [submitting, setSubmitting] = useState(false)
  const { errors, handleSubmit, register, reset, watch, setValue, getValues } =
    useForm(initialValues)
  const [arrivalDate, setArrivalDate] = useState(
    initialValues.user?.family?.arrivalDate
  )

  const unsubscribeFromAll = (e) => {
    const checked = e.target.checked
    const fields = getValues()
    Object.keys(fields).forEach((key) =>
      checked ? setValue(key, false) : reset(key)
    )
  }

  const renderPregnancyWeeklyForm = () => {
    if (watch('pregnancyWeekly')) {
      const currentYear = today.getFullYear()

      return (
        <Panel className="panel-bg-muted">
          <Panel.Body>
            <div className="pas">
              <div className="h6 text-bold mvn">Bedazzle your emails ✨</div>
              <p className="h6 mvs">
                Personalize your emails by entering your arrival date.
              </p>
              <div style={{ maxWidth: 400 }}>
                <DateSelect
                  label="Arrival Date"
                  years={range(currentYear - 20, currentYear + 2).reverse()}
                  onChange={(e) => setArrivalDate(e)}
                  value={arrivalDate}
                />
              </div>
            </div>
          </Panel.Body>
        </Panel>
      )
    }
  }

  const onSubmit = (emailPreferences) => {
    const data = {
      user: {
        emailPreferences: { ...emailPreferences },
        family: {
          arrivalDate,
        },
      },
    }
    submitData(data).then(() => {
      successMessage('Your communication preferences have been updated!')
    })
  }

  return (
    <div>
      <StyledSubHeading>Communication Preferences</StyledSubHeading>

      <Form onSubmit={handleSubmit(onSubmit)}>
        {user.isGiftGiverOnly ? null : (
          <div>
            <div className="h6">
              Eeny, meeny, miny, moe… Pick the very best emails that will help
              your family grow.
            </div>

            <div className="mbxl">
              <Checkbox
                label={<b>Week-by-Week Pregnancy and Parenting Emails </b>}
                help="Personalized emails about your body, your baby and what you really need to know."
                name="pregnancyWeekly"
                inputRef={register}
                defaultChecked={
                  initialValues.user.emailPreferences.pregnancyWeekly
                }
              />

              <Checkbox
                label={<b>Hello Baby Weekly Newsletter</b>}
                help="A once-a-week round-up of news and noteworthy stories from around the web."
                name="newsletter"
                inputRef={register}
                defaultChecked={initialValues.user.emailPreferences.newsletter}
              />
              <Checkbox
                label={<b>Registry & Shopping Emails</b>}
                help="Expert tips on how to build your baby registry and find gear for your baby's next stage, plus all the best sales."
                name="babyRegistry101"
                inputRef={register}
                defaultChecked={
                  initialValues.user.emailPreferences.babyRegistry101
                }
              />
              <Checkbox
                label={<b>Partner Emails</b>}
                help="Exclusive deals and information sent from Babylist on behalf of our partners."
                name="sponsored"
                inputRef={register}
                defaultChecked={initialValues.user.emailPreferences.sponsored}
              />
              <Checkbox
                label={<b>Announcements</b>}
                help="Changes to your registry, surveys and occasional updates about new Babylist features."
                name="announcements"
                inputRef={register}
                defaultChecked={
                  initialValues.user.emailPreferences.announcements
                }
              />
            </div>
          </div>
        )}

        {renderPregnancyWeeklyForm()}

        {user.isRegistryUser ? (
          <div>
            <StyledSubHeading className="h5">Registry Alerts</StyledSubHeading>
            <div className="mbl">
              <Checkbox
                label={<b>Purchase Alerts</b>}
                help="Get notified when someone purchases a gift from your registry."
                name="reservationAlerts"
                inputRef={register}
                defaultChecked={
                  initialValues.user.emailPreferences.reservationAlerts
                }
              />
              <Checkbox
                label={<b>Price & Stock Alerts</b>}
                help="Get notified when there are price or stock changes for items on your registry."
                name="priceStockAlerts"
                inputRef={register}
                defaultChecked={
                  initialValues.user.emailPreferences.priceStockAlerts
                }
              />
            </div>
          </div>
        ) : null}

        {user.isGiftGiver ? (
          <div>
            <StyledSubHeading className="h5">
              Gift Giver Emails
            </StyledSubHeading>
            <div className="mbl">
              <Checkbox
                label={<b>Gift Advice</b>}
                help="Age/stage appropriate clothes, toys and gear recommendations (plus deal alerts!) to help you give the best gifts. "
                name="giftGiverGiftAdvice"
                inputRef={register}
                defaultChecked={
                  initialValues.user.emailPreferences.giftGiverGiftAdvice
                }
              />
              <Checkbox
                label={<b>Partner Emails</b>}
                help="Exclusive deals and information sent from Babylist on behalf of our partners"
                name="giftGiverPartnerEmail"
                inputRef={register}
                defaultChecked={
                  initialValues.user.emailPreferences.giftGiverPartnerEmail
                }
              />
            </div>
          </div>
        ) : null}

        <hr className="mbl" />
        <Checkbox
          label={<b>Unsubscribe From All</b>}
          onChange={unsubscribeFromAll}
        />

        <hr className="mbl mtl" />
        <SaveButton submitting={submitting} pill>
          Save Changes
        </SaveButton>
      </Form>
    </div>
  )
}

const dueDateValidator = createValidator({
  'user.family.arrivalDate': [required],
})

const validate = (data) => {
  if (data.user.emailPreferences.pregnancyWeekly) {
    return dueDateValidator(data)
  }
  return {}
}

const mapStateToProps = (state, ownProps) => ({
  ...ownProps,
  initialValues: {
    user: {
      emailPreferences: state.user.emailPreferences,
      family: state.user.family,
      isGiftGiver: state.user.isGiftGiver,
      giftGiverRegistries: state.user.giftGiverRegistries,
    },
  },
  user: {
    email: state.user.email,
    isRegistryUser: state.user.registries.length !== 0,
    isGiftGiver: state.user.isGiftGiver,
    isGiftGiverOnly: state.user.isGiftGiverOnly,
    giftGiverRegistries: state.user.giftGiverRegistries,
  },
})

const mapDispatchToProps = (dispatch, ownProps) => ({
  submitData: (data) => ownProps._handleSubmit(data, dispatch),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EmailPreferencesForm)
